import React, { useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { Phone, Mail, MapPin, Instagram, Send, MessageCircle, Facebook } from 'lucide-react';
import { businessInfo } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const WHATSAPP_URL =
  'https://wa.me/355693520111?text=P%C3%ABrsh%C3%ABndetje%2C%20kam%20nj%C3%AB%20pyetje%20p%C3%ABr%20Lor95%20Boutique.';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(
        `${API}/api/contact`,
        formData
      );

      toast.success(
        'Mesazhi u dërgua me sukses. Do t’ju kontaktojmë së shpejti.',
        { duration: 3500 }
      );

      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });

    } catch (error) {
      const detail =
        error.response?.data?.detail;

      toast.error(
        typeof detail === 'string'
          ? detail
          : 'Mesazhi nuk u dërgua. Ju lutemi provoni përsëri.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Kontakt"
        description="Kontaktoni Lor95 Boutique për produkte, porosi dhe informacion. Porosit online me dërgesa në të gjithë Shqipërinë."
        path="/contact"
      />

      {/* Seksioni Kryesor */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden bg-gray-900">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src={businessInfo.storefrontImage}
            alt="Na Kontaktoni"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 text-center px-4">

          <h1 className="text-5xl sm:text-6xl font-serif font-bold text-white mb-4 animate-in fade-in slide-in-from-bottom duration-700">
            Na Kontaktoni
          </h1>

          <p className="text-xl text-white/90 animate-in fade-in slide-in-from-bottom duration-700 delay-150">
            Do të na bëhej shumë qejfi të dëgjonim nga ju!
          </p>

        </div>
      </section>


      {/* Përmbajtja e Kontaktit */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Informacioni i Kontaktit */}
            <div className="space-y-8">

              <div className="animate-in fade-in slide-in-from-left duration-700">

                <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                  Informacioni i Kontaktit
                </h2>

                <p className="text-lg text-gray-600 mb-8">
                  Keni pyetje rreth koleksioneve tona, dëshironi këshilla për stilin,
                  apo dëshironi të planifikoni një vizitë? Jemi këtu për t'ju ndihmuar!
                </p>

              </div>


              <div className="space-y-6">

                {/* Adresa */}
                <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-100">

                  <CardContent className="p-6 flex items-start space-x-4">

                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Na Vizitoni
                      </h3>

                      <p className="text-gray-600">
                        {businessInfo.address}
                      </p>

                      <p className="text-sm text-gray-500 mt-1">
                        {businessInfo.locationCode}
                      </p>

                    </div>

                  </CardContent>

                </Card>


                {/* Telefoni */}
                <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-200">

                  <CardContent className="p-6 flex items-start space-x-4">

                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
                        <Phone className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Na Telefononi
                      </h3>

                      <a 
                        href={`tel:${businessInfo.phone}`}
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        {businessInfo.phone}
                      </a>

                      <p className="text-sm text-gray-500 mt-1">
                        {businessInfo.hours}
                      </p>

                    </div>

                  </CardContent>

                </Card>



                {/* WhatsApp */}
                <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-300">

                  <CardContent className="p-6 flex items-start space-x-4">

                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center">
                        <MessageCircle className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        WhatsApp
                      </h3>

                      <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#148b46] transition-colors"
                      >
                        Na shkruani direkt në WhatsApp
                      </a>

                      <p className="text-sm text-gray-500 mt-1">
                        Për pyetje të shpejta rreth produkteve dhe porosive
                      </p>

                    </div>

                  </CardContent>

                </Card>


                {/* Instagram */}
                <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-300">

                  <CardContent className="p-6 flex items-start space-x-4">

                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gray-900 flex items-center justify-center">
                        <Instagram className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Na Ndiqni
                      </h3>

                      <a 
                        href={businessInfo.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-gray-900 transition-colors"
                      >
                        @lor95boutique
                      </a>

                      <p className="text-sm text-gray-500 mt-1">
                        Ndiqni koleksionet, modelet dhe risitë tona më të fundit
                      </p>

                    </div>

                  </CardContent>

                </Card>

                {/* Facebook */}
                <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-left duration-700 delay-500">
                  <CardContent className="p-6 flex items-start space-x-4">

                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-[#1877F2] flex items-center justify-center">
                        <Facebook className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Facebook
                      </h3>

                      <a
                        href="https://www.facebook.com/p/Lor-95-Boutique-100086001116286/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#1877F2] transition-colors"
                      >
                        Lor 95 Boutique në Facebook
                      </a>

                      <p className="text-sm text-gray-500 mt-1">
                        Na ndiqni për koleksionet dhe njoftimet më të reja
                      </p>
                    </div>

                  </CardContent>
                </Card>

              </div>


              {/* Orari i Punës */}
              <Card className="border-none shadow-md bg-gray-900 text-white animate-in fade-in slide-in-from-left duration-700 delay-400">

                <CardContent className="p-8">

                  <h3 className="text-xl font-semibold mb-4">
                    Orari i Punës
                  </h3>

                  <div className="space-y-2">

                    <div className="flex justify-between">
                      <span>E Hënë - E Diel</span>
                      <span className="font-semibold">Hapur çdo ditë</span>
                    </div>

                    <div className="flex justify-between">
                      <span>Ora e Mbylljes</span>
                      <span className="font-semibold">21:00</span>
                    </div>

                  </div>

                </CardContent>

              </Card>

            </div>


            {/* Formulari i Kontaktit */}
            <Card className="border-none shadow-xl animate-in fade-in slide-in-from-right duration-700">

              <CardContent className="p-8">

                <h2 className="text-3xl font-serif font-bold text-gray-900 mb-6">
                  Dërgoni një Mesazh
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">

                  {/* Emri */}
                  <div>

                    <Label htmlFor="name" className="text-gray-700 font-medium">
                      Emri dhe Mbiemri *
                    </Label>

                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-2 rounded-none border-gray-300 focus:border-gray-900"
                      placeholder="Vendosni emrin dhe mbiemrin"
                    />

                  </div>


                  {/* Email */}
                  <div>

                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Adresa Email *
                    </Label>

                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2 rounded-none border-gray-300 focus:border-gray-900"
                      placeholder="email@shembull.com"
                    />

                  </div>


                  {/* Telefoni */}
                  <div>

                    <Label htmlFor="phone" className="text-gray-700 font-medium">
                      Numri i Telefonit
                    </Label>

                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 rounded-none border-gray-300 focus:border-gray-900"
                      placeholder="+355 69 352 0111"
                    />

                  </div>


                  {/* Mesazhi */}
                  <div>

                    <Label htmlFor="message" className="text-gray-700 font-medium">
                      Mesazhi Juaj *
                    </Label>

                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="mt-2 rounded-none border-gray-300 focus:border-gray-900"
                      placeholder="Na tregoni se si mund t'ju ndihmojmë..."
                    />

                  </div>


                  {/* Butoni Dërgo */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    size="lg"
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-none py-6 transition-all duration-300 hover:scale-105"
                  >

                    {isSubmitting ? (
                      'Duke dërguar...'
                    ) : (
                      <>
                        Dërgo Mesazhin
                        <Send className="ml-2 h-5 w-5" />
                      </>
                    )}

                  </Button>

                </form>

              </CardContent>

            </Card>

          </div>
        </div>
      </section>


      {/* Seksioni CTA */}
      <section className="py-16 bg-white">

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
            Preferoni të Na Vizitoni Personalish?
          </h2>

          <p className="text-lg text-gray-600 mb-8">
            Ekipi ynë është gati t'ju mirëpresë në Lor95 Boutique.
            Ejani dhe zbuloni koleksionet tona për femra dhe meshkuj.
          </p>

          <Button
            asChild
            size="lg"
            className="bg-gray-900 hover:bg-gray-800 text-white px-8 rounded-none"
          >

            <a href="/visit">
              <MapPin className="mr-2 h-5 w-5" />
              Gjeni Vendndodhjen
            </a>

          </Button>

        </div>

      </section>

    </div>
  );
};

export default Contact;